import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useContext, useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Col, Container, Row } from 'react-bootstrap';
import Blockquote from '../../components/Blockquote';
import Button from '../../components/Button';
import FingertipsLinks from '../../components/FingertipsLinks/FingertipsLinks';
import GenericCard from '../../components/GenericCards';
import GenericList, { GenericListItem } from '../../components/GenericList';
import Label from '../../components/Label';
import { MobileCarousel } from '../../components/MobileCarousel/MobileCarousel';
import { RadioGroup } from '../../components/Radio';
import SelectableCard from '../../components/SelectableCard';
import Tabs from '../../components/Tabs/Tabs';
import useMobile from '../../hooks/useMobileHook';
import {
  SavingSummaryProvider,
  SavingSummaryStore,
} from '../../providers/savingSummary';
import {
  ACCOUNT_ACTIVITY_ROUTE,
  TRANSACTION_HISTORY_ROUTE,
} from '../../routes/types';
import {
  addOrRemoveDaysToGivenDate,
  DateFormats,
  formatDate,
  formatPrice,
} from '../../utils/utils';
import PopoverWithLinks from '../widgets/PopoverLinks/PopoverLinks';
import { Contributions } from './savingsSummaryTabPages/contributions';
import { Disbursements } from './savingsSummaryTabPages/disbursements';
import { Investments } from './savingsSummaryTabPages/investments';
import { Loans } from './savingsSummaryTabPages/loans';

const seriesData = [
  {
    name: 'Balance',
    type: 'area',
    data: [
      220000, 250000, 270000, 280000, 290000, 300000, 320000, 350000, 310000,
      340000, 345000, 350000,
    ],
  },
  {
    name: 'Contribute',
    type: 'area',
    data: [
      100, 500, 1000, 2500, 5000, 10000, 15000, 20000, 25000, 35000, 40000,
      45000,
    ],
  },
];
const generateOptions = () => ({
  chart: {
    height: 350,
    toolbar: {
      show: false,
    },
    data: seriesData,
    zoom: {
      enabled: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  legend: {
    show: true,
    offsetX: 20,
    offsetY: 7,
    markers: {
      width: 12,
      height: 12,
      radius: 0,
    },
  },
  toolbar: {
    show: false,
  },
  xaxis: {
    categories: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],

    tickAmount: 12,
  },
  colors: ['#2F55DD', '#9DB4C0'],

  // Please uncomment lines below, to hide the tooltip:
  // tooltip: {
  //   enabled: false,
  // },
  annotations: {
    xaxis: [
      {
        x: 0,
        strokeDashArray: 0,
        borderColor: '#165DFF',
      },
      {
        x: 0,
        x2: 100,
        fillColor: 'rgba(22, 93, 255, 0.2)',
        opacity: 0.4,
      },
    ],
  },
  yaxis: {
    type: 'number',
    opposite: true,
    title: {
      text: '',
    },
    labels: {
      formatter: function (a: any) {
        return `$${a.toLocaleString()}`;
      },
    },
  },

  zoom: {
    enabled: false,
  },
});

const today = new Date();

const currentYear = today.getFullYear();

const firstDayOfTheYear = new Date(currentYear, 0, 1);

const SavingsSummaryView = () => {
  const { fingertipLinks } = useContext(SavingSummaryStore) || {};
  const isMobile = useMobile();

  const tabsData = [
    {
      name: 'Investments',
      content: <Investments />,
    },
    {
      name: 'Contributions',
      content: <Contributions />,
    },
    {
      name: 'Loans',
      content: <Loans />,
    },
    {
      name: 'Disbursements',
      content: <Disbursements />,
    },
  ];

  const [series, setSeries] = useState<any>(seriesData);

  const [options, setOption] = useState(generateOptions());

  const linksData = [
    {
      linkHeader: 'Account activity',
      route: ACCOUNT_ACTIVITY_ROUTE,
    },

    {
      linkHeader: 'Balance breakdown',
      route: `#`,
    },

    {
      linkHeader: 'Custom account statement',
      route: '#',
    },

    {
      linkHeader: 'Loan/Withdrawal',
      route: `#`,
    },
  ];

  const plans = [
    {
      id: 1,
      title: '401 (k) Savings',
      balance: 341550.0,
      startingBalance: 311347,
      gainLoses: 7433,
    },
    {
      id: 2,
      title: 'Non-Qualified Plan',
      balance: 119256.0,
      startingBalance: 107801,
      gainLoses: 5022.6,
    },
    {
      id: 3,
      title: 'HSA',
      balance: 80059.0,
      startingBalance: 74112,
      gainLoses: 2127.73,
    },
  ];

  const [selectedPlan, setSelectedPlan] = useState<any>(plans[0]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('1');

  const [activity, setActivity] = useState<number>(0);

  const generateSeriesData = () => {
    const generatedSeriesData = [];
    const contribute = [];
    let currentBalance = selectedPlan.startingBalance;

    const sum = selectedPlan.startingBalance + selectedPlan.gainLoses;
    const activity = selectedPlan.balance - sum;

    // const percent = (1 / 100) * selectedPlan.startingBalance ;
    const percent = activity / 12;

    setActivity(activity);

    for (let i = 0; i < 12; i++) {
      currentBalance += percent;
      contribute.push(percent);
      generatedSeriesData.push(currentBalance);
    }
    generatedSeriesData[0] = selectedPlan.startingBalance;
    generatedSeriesData[generatedSeriesData.length - 1] = selectedPlan.balance;

    let newSeries = seriesData;
    newSeries[0].data = generatedSeriesData;
    newSeries[1].data = contribute;
    setSeries(newSeries);
    setOption(generateOptions());
  };

  const handlePlanChange = (plan: any) => {
    setSelectedPlan(plans.find((pl) => pl.id == plan));
    setSelectedPlanId(plan);
  };

  useEffect(() => {
    generateSeriesData();
  }, [selectedPlan]);
  return (
    <>
      <Container className={'cnd-custom-container-large mb-12 p-0'}>
        <Row>
          <Col className={'m-0 p-0 col-12'}>
            <Label className={'lh-xs'}>Total Balance</Label>
            <h4>
              {formatPrice(
                plans.reduce((prev, curr) => {
                  return prev + curr.balance;
                }, 0)
              )}
            </h4>
          </Col>
        </Row>

        <Row>
          {!isMobile && (
            <Col className={'m-0 p-0 col-10'}>
              <Label>
                <span className={'lh-xs fw-normal text-fs-base '}>
                  Select a plan to provide details
                </span>
              </Label>
              <Container className="m-0 p-0">
                <RadioGroup
                  name={'step-1'}
                  onChange={(plan) => handlePlanChange(plan)}
                  value={selectedPlanId}
                >
                  <Container className="m-0 p-0 d-flex gap-8">
                    {plans.map((plan) => {
                      return (
                        <SelectableCard
                          key={plan.id}
                          title={plan.title}
                          subtitle1={formatPrice(plan.balance)}
                          radioButtonValue={`${plan.id.toString()}`}
                          variant={'white'}
                          className="white-background"
                          selected={selectedPlan.id === plan.id}
                        />
                      );
                    })}
                  </Container>
                </RadioGroup>
              </Container>
            </Col>
          )}
          {isMobile && (
            <Col className={'col-12 mb-10'}>
              <Label className={'lh-xs'}>
                Select a plan to provide details
              </Label>
              <RadioGroup
                name={'step-1'}
                onChange={(plan) => handlePlanChange(plan)}
                value={'1'}
              >
                <Row className={'gap-2'}>
                  <MobileCarousel chunkedData={linksData}>
                    {plans.map((plan) => {
                      return (
                        <Col className={'col-4 m-0 p-0'}>
                          <SelectableCard
                            key={plan.id}
                            title={plan.title}
                            subtitle1={formatPrice(plan.balance)}
                            radioButtonValue={`${plan.id.toString()}`}
                            variant={'white'}
                            selected={selectedPlan.id === plan.id}
                          />
                        </Col>
                      );
                    })}
                  </MobileCarousel>
                </Row>
              </RadioGroup>
            </Col>
          )}
        </Row>
        <Row>
          <Col className={'col-12 col-lg-7 pe-lg-8'}>
            <Row>
              <Col
                className={
                  'p-0 m-0 d-flex justify-content-between align-items-center mb-4'
                }
              >
                <Col className={'ps-lg-0'}>
                  <h4 className={'text-sm-20-lg-32 m-0'}>
                    {selectedPlan.title}
                  </h4>
                  <Label className={'lh-xs pt-2 text-dark-gray-50'}>
                    <span className={'text-dark-gray-50'}>
                      {formatDate(
                        firstDayOfTheYear,
                        DateFormats.SHORT_MONTH_DAY_YEAR
                      )}{' '}
                      -{' '}
                      {formatDate(
                        addOrRemoveDaysToGivenDate(today, -1),
                        DateFormats.SHORT_MONTH_DAY_YEAR
                      )}{' '}
                    </span>
                  </Label>
                </Col>
                <PopoverWithLinks linksData={linksData} />
              </Col>
            </Row>

            <Row>
              <Col className="p-0 border rounded-2 pb-8 pt-8 bg-white col-12 ps-lg-0">
                <Container>
                  <Row>
                    <Col className={'d-flex flex-column col-lg-3 col-12'}>
                      <Row className="align-items-center">
                        <Col className={'col-8 col-lg-12'}>
                          <Label className={'lh-xs  blue-text'}>
                            Ending Balance
                          </Label>
                        </Col>
                        <Col className={'col-4 col-lg-12'}>
                          <h4 className={'text-sm-20-lg-32 m-0 text-primary'}>
                            {formatPrice(selectedPlan.balance)}
                          </h4>
                        </Col>
                      </Row>
                    </Col>
                    <Col
                      className={
                        'd-flex flex-column col-lg-3 col-12 pt-lg-5 pt-0'
                      }
                    >
                      <Row className="align-items-center text-lg-center">
                        <Col className={'col-8 col-lg-12'}>
                          <Label
                            className={
                              'cursor-pointer text-ligther-grey  lh-xs d-block'
                            }
                          >
                            <span className="text-dark-gray-50 w-100">
                              Starting Balance
                            </span>
                          </Label>
                        </Col>
                        <Col className={'col-4 col-lg-12'}>
                          <h6 className={'text-sub lh-xs'}>
                            {formatPrice(selectedPlan.startingBalance || 0)}
                          </h6>
                        </Col>
                      </Row>
                    </Col>
                    <Col
                      className={
                        'd-flex flex-column col-lg-3 col-12 pt-lg-5 pt-0'
                      }
                    >
                      <Row className="align-items-center text-lg-center">
                        <Col className={'col-8 col-lg-12'}>
                          <Label
                            className={
                              'cursor-pointer text-ligther-grey  lh-xs d-block'
                            }
                          >
                            <span className="text-dark-gray-50 w-100">
                              Activity
                            </span>
                          </Label>
                        </Col>
                        <Col className={'col-4 col-lg-12'}>
                          <h6 className={'text-sub lh-xs'}>
                            +{formatPrice(activity)}
                          </h6>
                        </Col>
                      </Row>
                    </Col>
                    <Col
                      className={
                        'd-flex flex-column col-lg-3 col-12  pt-lg-5 pt-0'
                      }
                    >
                      <Row className="align-items-center text-lg-center">
                        <Col className={'col-8 col-lg-12'}>
                          <Label
                            className={
                              'cursor-pointer text-ligther-grey  lh-xs  d-block'
                            }
                          >
                            <span className="text-dark-gray-50 w-100">
                              Gain/Losses
                            </span>
                          </Label>
                        </Col>
                        <Col className={'col-4 col-lg-12'}>
                          <h6 className="text-sub lh-xs bold-text text-green">
                            +{formatPrice(selectedPlan.gainLoses)}
                          </h6>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className={'overflow-auto'}>
                    <Col
                      className={'overflow-hidden'}
                      style={{
                        minWidth: '650px',
                      }}
                    >
                      <ReactApexChart
                        options={options}
                        type="area"
                        curve="smooth"
                        series={series}
                      />
                    </Col>
                  </Row>
                  <Blockquote>
                    <span className="text-dark-gray">
                      You are fully vested!
                    </span>
                  </Blockquote>
                </Container>
              </Col>
            </Row>
          </Col>
          <Col className={'p-0 m-0 col-12 col-lg-5 mt-10 mt-lg-0 ps-lg-4'}>
            <Row>
              <Col className={'col-12 mb-5'}>
                <FingertipsLinks data={fingertipLinks} />
              </Col>

              <Col className={'col-12 mt-10'}>
                <h4 className={'text-sm-20-lg-32 m-0'}>
                  {'Recent Transactions'}
                </h4>

                <GenericCard variant={'white'} className={'p-10 mt-5 mt-lg-8'}>
                  <div className={'text-dark-gray text-fs-base fw-normal mb-6'}>
                    {'Recent Transactions'}
                  </div>
                  <GenericList>
                    <GenericListItem>
                      <div
                        className={
                          'w-100 d-flex justify-content-between align-items-center'
                        }
                      >
                        <Label className={'cursor-pointer text-blue'}>
                          <span>Change Investment</span>
                        </Label>
                        <Label>
                          <span className={'ps-3 text-ligther-grey'}>
                            {'Completed'}
                          </span>
                        </Label>
                      </div>
                    </GenericListItem>
                    <GenericListItem>
                      <div
                        className={
                          'w-100 d-flex justify-content-between align-items-center'
                        }
                      >
                        <Label>{'Change Contribution'}</Label>
                        <span className={'ps-3 text-ligther-grey'}>
                          {'Completed'}
                        </span>
                      </div>
                    </GenericListItem>
                    <GenericListItem>
                      <span className={'text-dark-gray-50 lh-sm'}>
                        {'For all activity check Transaction History.'}
                      </span>
                    </GenericListItem>
                  </GenericList>
                  <div className={'text-end'}>
                    <Button
                      size={'sm'}
                      rightIcon={light('list-timeline')}
                      href={`#${TRANSACTION_HISTORY_ROUTE}`}
                    >
                      {'Transaction History'}
                    </Button>
                  </div>
                </GenericCard>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col className={'col-12 col-lg-7 mt-4 pe-lg-8'}>
            <Row>
              <Col className={'p-0 m-0 col-12 mb-2'}>
                <Label>
                  <h4 className={'text-14 m-0'}>{'Rate of Return'}</h4>
                </Label>
              </Col>
              <Col className={'col-12 border rounded-2 pb-10 px-10'}>
                <Label className={'lh-xs'}>{'Your Current ROR'}</Label>
                <h4>6.28%</h4>
                <Blockquote className={'mb-5 '}>
                  <span className={'text-dark-gray fw-bold'}>
                    Did you know?
                  </span>
                  <ul className="text-dark-gray">
                    <li>
                      Your rate of return 6.28% is lower than the age
                      appropriate target fund at 10.7%
                    </li>
                    <li>
                      <span className={'text-blue'}>
                        Learn about ways you can improve your ROR
                      </span>
                    </li>
                    <li>
                      <span className={'text-blue'}>
                        Consider the Model Mix
                      </span>
                    </li>
                  </ul>
                </Blockquote>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row>
          <Col className="m-0 p-0 mt-12">
            <Tabs tabs={tabsData} variant={'horizontal'} />
          </Col>
        </Row>
      </Container>
    </>
  );
};

const SavingsSummary = () => {
  return (
    <SavingSummaryProvider>
      <SavingsSummaryView />
    </SavingSummaryProvider>
  );
};

export default SavingsSummary;
